import React, { useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Divider } from '@mui/material';
import { 
  ColorLensOutlined,
  FormatAlignJustifyOutlined,
  FormatAlignLeftOutlined,
  FormatAlignCenterOutlined,
  FormatAlignRightOutlined,
  FormatBoldOutlined,
  FormatItalicOutlined,
  FormatUnderlinedOutlined,
  StrikethroughSOutlined,
  InsertLink,
  FormatPaintOutlined,
} from '@mui/icons-material';
import { BubbleMenu, Editor } from '@tiptap/react';
import { 
  useTextMenuCommands, 
  useTextFormatStates, 
  useTextFormatOptions, 
} from '../../../../utils/tipTapEditor';
import TextFormatMenu from './TextFormatMenu';
import MoreFormatOptionsMenu from './MoreFormatOptionsMenu';
import TextFormatSelect from './TextFormatSelect';
import { borderRadius, palette, spaces } from '../../../../assets/styles/themes/tokens';
import FontSizePicker from './FontSizePicker';
import useAnchorEl from './hooks/useAnchor';
import ColorPickerMenu from './ColorPickerMenu';
import { useTranslation } from 'react-i18next';
import ContextLinkMenu from './ContextLinkMenu';

interface ContextMenuProps {
    editor: Editor;
}

export interface MenuOption {
  id: string;
  dataTestId: string;
  icon: JSX.Element;
  isActive: boolean;
  onClick: () => void;
}

export interface FontSizeOption {
  label: string;
  value: string;
}

const StyledBubbleMenu = styled(BubbleMenu)(() => ({
  padding: spaces.xxs,
  borderRadius: borderRadius.sm,
  backgroundColor: 'white',
  border: `1px solid ${palette.divider}`,
}));

const ContextMenu = ({ editor }: ContextMenuProps) => {
  const { t } = useTranslation();
  
  const commands = useTextMenuCommands(editor);
  const states = useTextFormatStates(editor);
  const textFormatOptions = useTextFormatOptions(editor);

  const moreOptions = useAnchorEl();
  const contentTypePicker = useAnchorEl();
  const fontSizePicker = useAnchorEl();
  const linkMenu = useAnchorEl();

  const colorText = useAnchorEl();
  const highlightText = useAnchorEl();

  const textFormatMenuOptions = [
    { 
      id: t('Bold'), 
      icon: <FormatBoldOutlined />, 
      onClick: commands.onBold, 
      isActive: states.isBold,
      dataTestId: 'bold'
    },
    { 
      id: t('Italic'), 
      icon: <FormatItalicOutlined />, 
      onClick: commands.onItalic, 
      isActive: states.isItalic,
      dataTestId: 'italic'
    },
    { 
      id: t('Underline'), 
      icon: <FormatUnderlinedOutlined />, 
      onClick: commands.onUnderline, 
      isActive: states.isUnderline,
      dataTestId: 'underline'
    },
    { 
      id: t('Strikethrough'), 
      icon: <StrikethroughSOutlined />, 
      onClick: commands.onStrike, 
      isActive: states.isStrike,
      dataTestId: 'strike'
    },
  ];

  const moreFormatOptions = [
    { 
      id: t('Left Aligned'), 
      icon: <FormatAlignLeftOutlined />, 
      onClick: commands.onAlignLeft, 
      isActive: states.isAlignLeft,
      dataTestId: 'align-left'
    },
    { 
      id: t('Centered'), 
      icon: <FormatAlignCenterOutlined />, 
      onClick: commands.onAlignCenter, 
      isActive: states.isAlignCenter,
      dataTestId: 'align-center'
    },
    { 
      id: t('Right Aligned'), 
      icon: <FormatAlignRightOutlined />, 
      onClick: commands.onAlignRight, 
      isActive: states.isAlignRight,
      dataTestId: 'align-right'
    },
    { 
      id: t('Justify'), 
      icon: <FormatAlignJustifyOutlined />, 
      onClick: commands.onAlignJustify, 
      isActive: states.isAlignJustify,
      dataTestId: 'align-justify',
    },
  ];

  const fontSizeOptions: FontSizeOption[] = [
    { label: t('Smaller'), value: spaces.xs },
    { label: t('Small'), value: spaces.sm },
    { label: t('Medium'), value: spaces.md },
    { label: t('Large'), value: spaces.lg },
    { label: t('Extra Large'), value: spaces.xl }
  ];

  useEffect(() => {
    const handleSelectionChange = () => {
      if (!editor.isActive('link')) {
        linkMenu.handleClose();
      }
    };

    editor.on('selectionUpdate', handleSelectionChange);

    return () => {
      editor.off('selectionUpdate', handleSelectionChange);
    };
  }, [editor, linkMenu]);

  return (
    <StyledBubbleMenu
      tippyOptions={{ popperOptions: { placement: 'top-start' }, zIndex: 1000 , maxWidth: 'none' }}
      editor={editor}
      pluginKey='textMenu'
      shouldShow={states.shouldShow}
      updateDelay={100}
    > 
      <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: spaces.xxs,
      }}>
        <FontSizePicker
          options={fontSizeOptions}
          anchorEl={fontSizePicker.anchorEl}
          handleClick={fontSizePicker.handleClick}
          handleClose={fontSizePicker.handleClose}
          handleFontSizeChange={commands.onSetFontSize} 
          value={states.currentSize || ''}
        />
        <TextFormatSelect
          anchorEl={contentTypePicker.anchorEl}
          textFormatOptions={textFormatOptions}
          handleClick={contentTypePicker.handleClick}
          handleClose={contentTypePicker.handleClose}
        />
        <Divider orientation='vertical' flexItem />
        <TextFormatMenu
          options={textFormatMenuOptions}
        />
        <ContextLinkMenu
          editor={editor}
          id={t('Link')}
          anchorEl={linkMenu.anchorEl}
          handleClick={linkMenu.handleClick}
          handleClose={linkMenu.handleClose}
          icon={<InsertLink />}
        />
        <ColorPickerMenu
          id={t('Highlight')}
          dataTestId='text-highlight'
          icon={<FormatPaintOutlined />}
          anchorEl={highlightText.anchorEl}
          handleClick={highlightText.handleClick}
          handleClose={highlightText.handleClose}
          currentColor={states.currentHighlight}
          onChangeColor={commands.onChangeHighlight}
          onClearColor={() => {
            commands.onClearHighlight();
            highlightText.handleClose();
          }}
        />
        <ColorPickerMenu
          id={t('Text Color')}
          dataTestId='text-color'
          icon={<ColorLensOutlined />}
          anchorEl={colorText.anchorEl}
          handleClick={colorText.handleClick}
          handleClose={colorText.handleClose}
          currentColor={states.currentColor}
          onChangeColor={commands.onChangeColor}
          onClearColor={() => {
            commands.onClearColor();
            colorText.handleClose();
          }}
        />
        <MoreFormatOptionsMenu 
          options={moreFormatOptions}
          anchorEl={moreOptions.anchorEl}
          handleClick={moreOptions.handleClick}
          handleClose={moreOptions.handleClose}
        />
      </Box>
    </StyledBubbleMenu>
  );
};

export default ContextMenu;
